import { debounce } from "lodash";
import { useCallback, useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";

import { OptionalAmountValidation } from "@krea/customer-features/form-validation/RequestedLoanAmountValidation";
import {
  useFormatCurrency,
  useLeasingObjectsList,
} from "@krea/customer-features/hooks";
import { settings } from "@krea/customer-features/settings";
import { Error } from "@krea/customer-features/shared-components/fields/error";
import Fieldset from "@krea/customer-features/shared-components/fields/fieldset";
import LeasingAmount from "@krea/customer-features/shared-components/loan-application-form/fields/leasing/amount";
import LeasingObject from "@krea/customer-features/shared-components/loan-application-form/fields/leasing/object";
import { usePostFieldRequest } from "@krea/customer-features/store/field-requests/hooks/mutations";
import { useGetOneFieldRequest } from "@krea/customer-features/store/field-requests/hooks/queries";
import {
  LOAN_APPLICATION_STATES,
  numberFromFormat,
} from "@krea/customer-features/utils";

import FieldRequestPreviewHeader from "../FieldRequestPreviewHeader";
import FieldRequestSaved from "../FieldRequestSaved";

const LeasingForm = ({
  applicationState,
  fieldRequestID,
  isPreviewMode,
  isLeasing,
}) => {
  const { t } = useTranslation();
  const leasingObjectsList = useLeasingObjectsList();
  const [formattedCurrencyValue] = useFormatCurrency({
    currency: settings.currency,
  });

  const { data: { displayName, submittedData } = {} } =
    useGetOneFieldRequest(fieldRequestID);
  const { mutate: postFieldRequest, error } =
    usePostFieldRequest(fieldRequestID);

  const errors = error?.data?.errors || [];

  function reducer(state, action) {
    switch (action.type) {
      case "leasing_amount":
        return { ...state, leasing_amount: action.payload };

      case "leasing_product_type":
        return { ...state, leasing_product_type: action.payload };

      case "touched":
        return { ...state, touched: action.payload };

      default:
        return { ...state };
    }
  }

  const [state, setState] = useReducer(reducer, {
    leasing_amount: submittedData?.leasing_amount || "",
    leasing_product_type: submittedData?.leasing_product_type || "",
    touched: false,
    isValidUrlLink: true,
  });

  const [validationError, setValidationError] = useState("");

  const createFormData = (value) => {
    const formData = new FormData();
    formData.append("leasing_amount", value.leasing_amount || "");
    formData.append("leasing_product_type", value.leasing_product_type || "");

    return formData;
  };

  const onBlur = (state, submittedData) => {
    const { touched } = state;
    const value = { ...state };

    if (touched) {
      try {
        // Set validated amount
        value.leasing_amount = OptionalAmountValidation().validateSync(
          value?.leasing_amount ?? null,
        );
      } catch (error) {
        // if we have an invalid amount, we set it to old submittedData value or null
        value.leasing_amount = submittedData?.leasing_amount ?? null;
      }

      const formData = createFormData(value);
      postFieldRequest(formData);

      if (touched) {
        setState({ type: "touched", payload: false });
      }
    }
  };

  const onChange = ({ target }) => {
    let parsedValue = target.value;

    if (target.name === "leasing_amount") {
      parsedValue = numberFromFormat(parsedValue);

      try {
        parsedValue = OptionalAmountValidation().validateSync(parsedValue);
        setValidationError("");
      } catch (e) {
        setValidationError(e?.errors?.[0]);
      }
    }
    setState({ type: `${target.name}`, payload: parsedValue });
    setState({ type: "touched", payload: state.value !== parsedValue });
  };

  const debouncedFunc = debounce(
    (state, submittedData) => onBlur(state, submittedData),
    2500,
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    (state, submittedData) => debouncedFunc(state, submittedData),
    [],
  );

  useEffect(() => {
    debouncedSave(state, submittedData);

    return () => {
      debouncedFunc.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, submittedData]);

  const typeOfObjectElement = leasingObjectsList?.find(
    (item) => item.value === state.leasing_product_type,
  );
  const typeOfObjectValue = typeOfObjectElement?.display_text;

  // edit only if application is before OPEN_FOR_BIDS
  const editAmount =
    [
      LOAN_APPLICATION_STATES.DRAFT,
      LOAN_APPLICATION_STATES.CREATED,
      LOAN_APPLICATION_STATES.ADVICE,
    ].includes(applicationState) ||
    (!isLeasing && applicationState === LOAN_APPLICATION_STATES.OPEN_FOR_BIDS);

  return (
    <>
      {isPreviewMode ? (
        <>
          <FieldRequestPreviewHeader
            label={displayName}
            value={t("iaq.leasing.preview.amount", {
              amount: formattedCurrencyValue(state.leasing_amount),
            })}
          />

          {typeOfObjectValue && (
            <FieldRequestPreviewHeader
              subheader
              label={t("iaq.leasing.preview.objectType")}
              value={typeOfObjectValue}
            />
          )}
        </>
      ) : (
        <>
          <div className="input-form mb-5">
            <Fieldset>
              <LeasingObject
                name="leasing_product_type"
                label={t(
                  "applicationForm.leasing.fields.objectType.label.fieldRequest",
                )}
                objects={leasingObjectsList}
                value={state.leasing_product_type || ""}
                onChange={onChange}
                onBlur={() => onBlur(state, submittedData)}
              />
              <LeasingAmount
                name="leasing_amount"
                value={state.leasing_amount || ""}
                disabled={!editAmount}
                onChange={editAmount && onChange}
                error={validationError}
                onBlur={() => editAmount && onBlur(state, submittedData)}
              />
            </Fieldset>
          </div>

          <FieldRequestSaved fieldRequestID={fieldRequestID} />
        </>
      )}

      {errors.map((error, index) => (
        <Error className="mt-3" key={index}>
          {error.message}
        </Error>
      ))}
    </>
  );
};

export default LeasingForm;
